<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  BookOpen,
  Check,
  Cloud,
  Sparkles,
  Star,
  Users,
  X,
} from 'lucide-vue-next'
import { useGating, FREE_LIMITS, type PaywallGate } from '@/composables/useGating'
import { usePricing } from '@/composables/usePricing'
import { useAnalytics } from '@/composables/useAnalytics'

/**
 * ProPaywallDialog — the single Pro upsell shown across the app.
 *
 * Mirrors Android's ProPaywallSheet. The dialog's open state is driven by
 * `useGating().paywallGate`; any feature can pop it by calling
 * `showPaywall('classes' | 'scheme' | 'ai' | 'storage')`. The contextual
 * hero is picked from the gate; the four-row feature list is identical
 * regardless so users can see the full picture.
 *
 * Mount this once at app shell level (AppLayout).
 */

const router = useRouter()
const { capture } = useAnalytics()
const { paywallGate, hidePaywall } = useGating()
const { ctaText, priceLabel, trialBadge, promotionBanner, hasPromotion } = usePricing()

interface GateHero {
  icon: typeof Users
  title: string
  subtitle: string
}

const HEROES: Record<PaywallGate, GateHero> = {
  classes: {
    icon: Users,
    title: `You've reached the ${FREE_LIMITS.classes}-class limit`,
    subtitle:
      'Upgrade to Pro for unlimited classes — plus uncapped planning, AI, and 5 GB of storage.',
  },
  scheme: {
    icon: BookOpen,
    title: 'Want another planning scheme?',
    subtitle:
      `Free plans include ${FREE_LIMITS.scheme} planning schemes. Upgrade for unlimited schemes, plus uncapped classes, AI, and 5 GB of storage.`,
  },
  ai: {
    icon: Sparkles,
    title: `You've used your ${FREE_LIMITS.ai} AI requests this month`,
    subtitle:
      'Upgrade for unlimited AI generation — courses, units, lessons, reports — plus uncapped classes and 5 GB of storage.',
  },
  storage: {
    icon: Cloud,
    title: 'Storage is full',
    subtitle:
      `You've reached your ${FREE_LIMITS.storageMb} MB free storage. Upgrade for 5 GB — plus uncapped classes, planning, and AI.`,
  },
  feature: {
    icon: Star,
    title: 'Upgrade to Pro',
    subtitle:
      'Get uncapped classes, planning, AI, and 5 GB of storage. One simple plan.',
  },
}

const isOpen = computed(() => paywallGate.value !== null)
const hero = computed<GateHero | null>(() =>
  paywallGate.value ? HEROES[paywallGate.value] : null,
)

const features = computed(() => [
  {
    icon: Users,
    label: 'Classes',
    free: `${FREE_LIMITS.classes} classes`,
    pro: 'Unlimited',
  },
  {
    icon: BookOpen,
    label: 'Planning schemes',
    free: `${FREE_LIMITS.scheme} schemes`,
    pro: 'Unlimited',
  },
  {
    icon: Sparkles,
    label: 'AI generation',
    free: `${FREE_LIMITS.ai} / month`,
    pro: 'Unlimited',
  },
  {
    icon: Cloud,
    label: 'Cloud storage',
    free: `${FREE_LIMITS.storageMb} MB`,
    pro: '5 GB',
  },
])

watch(
  paywallGate,
  (gate) => {
    if (gate) capture('pro_gate_shown', { feature: gate })
  },
)

function close() {
  if (paywallGate.value) capture('pro_gate_dismissed', { feature: paywallGate.value })
  hidePaywall()
}

function onUpgrade() {
  if (paywallGate.value)
    capture('upgrade_cta_tapped', { source: 'paywall', feature: paywallGate.value })
  hidePaywall()
  router.push('/checkout')
}

function onLearnMore() {
  if (paywallGate.value) capture('compare_plans_tapped', { feature: paywallGate.value })
  hidePaywall()
  router.push('/settings/upgrade')
}
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen && hero" class="paywall-root">
        <div class="paywall-scrim" @click="close" />

        <Transition
          appear
          enter-active-class="transition ease-out duration-200"
          enter-from-class="translate-y-4 sm:translate-y-0 sm:scale-95 opacity-0"
          enter-to-class="translate-y-0 sm:scale-100 opacity-100"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="translate-y-0 sm:scale-100 opacity-100"
          leave-to-class="translate-y-4 sm:translate-y-0 sm:scale-95 opacity-0"
        >
          <div class="paywall-card" role="dialog" aria-modal="true">
            <button type="button" class="paywall-close" aria-label="Close" @click="close">
              <X :size="18" :stroke-width="2" />
            </button>

            <div class="paywall-hero">
              <span class="paywall-hero-icon">
                <component :is="hero.icon" :size="28" :stroke-width="2" />
              </span>
              <h2 class="paywall-hero-title">{{ hero.title }}</h2>
              <p class="paywall-hero-subtitle">{{ hero.subtitle }}</p>
            </div>

            <ul class="paywall-features">
              <li v-for="(f, i) in features" :key="i" class="paywall-feature">
                <span class="paywall-feature-icon">
                  <component :is="f.icon" :size="16" :stroke-width="2" />
                </span>
                <span class="paywall-feature-label">{{ f.label }}</span>
                <span class="paywall-feature-free">Free: {{ f.free }}</span>
                <span class="paywall-feature-pro">
                  <Check :size="14" :stroke-width="3" />
                  {{ f.pro }}
                </span>
              </li>
            </ul>

            <p v-if="hasPromotion" class="paywall-promo">{{ promotionBanner }}</p>

            <div class="paywall-actions">
              <button type="button" class="paywall-cta" @click="onUpgrade">
                {{ ctaText || 'Upgrade to Pro' }}
              </button>
              <p v-if="trialBadge || priceLabel" class="paywall-cta-sub">
                <template v-if="trialBadge">{{ trialBadge }} · then {{ priceLabel }}</template>
                <template v-else>{{ priceLabel }} · cancel anytime</template>
              </p>
              <button type="button" class="paywall-secondary" @click="onLearnMore">
                See full comparison
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.paywall-root {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-lg);
}

.paywall-scrim {
  position: absolute;
  inset: 0;
  background: rgba(12, 12, 14, 0.5);
  backdrop-filter: blur(4px);
}

.paywall-card {
  position: relative;
  width: 100%;
  max-width: 28rem;
  background: var(--surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-modal);
  padding: var(--space-xl) var(--space-lg) var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
}

.paywall-close {
  position: absolute;
  top: var(--space-sm);
  right: var(--space-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  background: transparent;
  border: 0;
  color: var(--ink-600);
  cursor: pointer;
  transition: background-color var(--motion-fast), color var(--motion-fast);
}

.paywall-close:hover {
  background: var(--surface-muted);
  color: var(--ink-900);
}

.paywall-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-sm);
}

.paywall-hero-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  background: var(--coral-soft);
  color: var(--coral);
}

.paywall-hero-title {
  margin: 0;
  font-size: var(--text-title-md);
  line-height: var(--text-title-md-lh);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-tight);
  color: var(--ink-900);
}

.paywall-hero-subtitle {
  margin: 0;
  font-size: var(--text-body-sm);
  line-height: var(--text-body-sm-lh);
  color: var(--ink-600);
  max-width: 360px;
}

.paywall-features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.paywall-feature {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-sm) 0;
  border-bottom: 1px solid var(--hairline);
}

.paywall-feature:last-child {
  border-bottom: 0;
}

.paywall-feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm);
  background: var(--surface-muted);
  color: var(--ink-700);
}

.paywall-feature-label {
  font-size: var(--text-body);
  font-weight: var(--weight-medium);
  color: var(--ink-900);
}

.paywall-feature-free {
  font-size: var(--text-label);
  color: var(--ink-500);
  font-variant-numeric: tabular-nums;
}

.paywall-feature-pro {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--text-body-sm);
  font-weight: var(--weight-semibold);
  color: var(--success-ink);
  font-variant-numeric: tabular-nums;
}

.paywall-promo {
  margin: 0;
  padding: var(--space-sm) var(--space-md);
  text-align: center;
  background: var(--warning-soft);
  color: var(--warning-ink);
  border-radius: var(--radius-md);
  font-size: var(--text-body-sm);
  font-weight: var(--weight-semibold);
}

.paywall-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-sm);
}

.paywall-cta {
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  background: var(--ink-black);
  color: var(--ink-white);
  border: 0;
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: var(--text-body);
  font-weight: var(--weight-semibold);
  cursor: pointer;
  transition: background-color var(--motion-fast), transform var(--motion-fast);
}

.paywall-cta:hover {
  background: var(--ink-900);
}

.paywall-cta:active {
  transform: scale(0.99);
}

.paywall-cta-sub {
  margin: 0;
  font-size: var(--text-label);
  color: var(--ink-500);
  text-align: center;
}

.paywall-secondary {
  background: transparent;
  border: 0;
  font-family: inherit;
  font-size: var(--text-body-sm);
  font-weight: var(--weight-medium);
  color: var(--ink-700);
  cursor: pointer;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  transition: color var(--motion-fast), background-color var(--motion-fast);
}

.paywall-secondary:hover {
  color: var(--ink-900);
  background: var(--surface-muted);
}
</style>
